import type { Bookmark } from '@td/shared';

/**
 * A quick link, as a tile.
 *
 * Every tile is the same size, and the label always fits inside it. That
 * second part is the whole design problem: the tiles were sized by eye and
 * "ParentSquare" ran off the right-hand edge while "1st Grade Curriculum" had
 * its second line sliced in half. Knowing the icon is not the same as being
 * able to read the name — you recognise the icon because you put it there
 * last August, not because it says what it is.
 *
 * So the height is arithmetic rather than a guess. Each size states its icon,
 * its line height and how many lines it allows, and the box is exactly big
 * enough for all of it plus its padding. Change one number and the tile grows
 * to suit rather than quietly clipping.
 */

interface TileSize {
  width: number;
  icon: number;
  /** Pixels per line of the label. */
  lineHeight: number;
  lines: number;
  /** Space above the icon and below the last line. */
  padding: number;
  /** Between the icon and the first line. */
  gap: number;
}

const SIZES: Record<'normal' | 'small', TileSize> = {
  normal: { width: 150, icon: 36, lineHeight: 17, lines: 2, padding: 10, gap: 6 },
  small: { width: 122, icon: 30, lineHeight: 15, lines: 2, padding: 8, gap: 5 },
};

function tileHeight(s: TileSize): number {
  return s.padding * 2 + s.icon + s.gap + s.lineHeight * s.lines;
}

export function BookmarkTile({
  bookmark,
  size = 'normal',
}: {
  bookmark: Bookmark;
  size?: 'normal' | 'small';
}) {
  const s = SIZES[size];

  /*
   * Longer labels step down a size so more of them fit on a line — but never
   * below the line height the box was measured with, so a smaller font buys
   * room across rather than making the text overflow downwards.
   */
  const len = bookmark.label.length;
  const fontSize = size === 'small' ? (len > 18 ? 10 : 11) : len > 22 ? 11 : len > 14 ? 12 : 13;

  return (
    <div
      style={{
        width: s.width,
        height: tileHeight(s),
        paddingTop: s.padding,
        paddingBottom: s.padding,
        rowGap: s.gap,
      }}
      className="flex flex-col items-center justify-center rounded-card border border-sand/70 bg-white px-1.5 shadow-soft transition-colors group-hover:border-clay group-hover:bg-linen/60"
    >
      {bookmark.hasImage ? (
        <img
          // The version in the query string busts the cache when it changes.
          src={`/api/bookmarks/${bookmark.id}/image?v=${bookmark.imageVersion}`}
          alt=""
          style={{ width: s.icon, height: s.icon }}
          className="shrink-0 rounded object-cover"
          loading="lazy"
        />
      ) : bookmark.iconType === 'emoji' && bookmark.emoji ? (
        <span style={{ fontSize: s.icon }} className="shrink-0 leading-none">
          {bookmark.emoji}
        </span>
      ) : null}

      <span
        style={{
          fontSize,
          lineHeight: `${s.lineHeight}px`,
          height: s.lineHeight * s.lines,
          WebkitLineClamp: s.lines,
        }}
        /*
         * `break-words` is what stops a single long word — ParentSquare,
         * PowerSchool — running past the edge; clamping only ever handles
         * whole lines, and a word with nowhere to break is one line however
         * wide it is.
         */
        className="line-clamp-2 w-full break-words text-center text-espresso"
        title={bookmark.label}
      >
        {bookmark.label}
      </span>
    </div>
  );
}
